import { ReactElement, useEffect, useMemo, useState } from 'react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';
import { useInfiniteScroll } from '@heroui/use-infinite-scroll';
import { Autocomplete } from '@ds';
import { isNull } from 'lodash-es';
import { PaginatedBody, SearchQuery } from '@/api/types';
import { SelectItemType } from '@/types';
import { useInfiniteList } from '@/hooks/useInfiniteList';
import { getInputValidationErrorProps } from '@/utils/helpers';

type CaseFacilityAutocompleteProps<F extends FieldValues, R extends Record<string, unknown> = SelectItemType> = {
  name: Path<F>;
  placeholder: string;
  ariaLabel: string;
  control: Control<F>;
  infiniteListQuery: (filters: SearchQuery) => UseInfiniteQueryResult<InfiniteData<PaginatedBody<R>, unknown>, Error>;
  renderListItem: (item: object) => ReactElement;
  onSelect: (item?: R) => void;
  onClear?: () => void;
  defaultInputValue?: string;
  isDisabled?: boolean;
  filters?: Record<string, unknown>;
  getDisplayName?: (item: R) => string;
  formatSearchValue?: (value: string) => string;
  sortItems?: (a: R, b: R) => number;
  // When true, shows validation errors immediately instead of waiting for field touch
  validateOnSubmit?: boolean;
};

export const CaseFacilityAutocomplete = <F extends FieldValues, R extends Record<string, unknown>>({
  name,
  placeholder,
  ariaLabel,
  control,
  infiniteListQuery,
  renderListItem,
  onSelect,
  onClear,
  defaultInputValue = '',
  isDisabled,
  filters = {},
  getDisplayName,
  formatSearchValue,
  sortItems,
  validateOnSubmit = false,
}: CaseFacilityAutocompleteProps<F, R>) => {
  const [selectedItem, setSelectedItem] = useState<R | null>(null);
  const [inputValue, setInputValue] = useState(defaultInputValue);
  const [isAutocompleteOpen, setAutocompleteOpen] = useState(false);
  const { list, isLoading, hasNextPage, fetchNextPage } = useInfiniteList({
    query: infiniteListQuery,
    queryParams: {
      itemsPerPage: 20,
      orderBy: 'name',
      asc: true,
      ...filters,
    },
    searchTerm: formatSearchValue ? formatSearchValue(inputValue) : inputValue,
  });

  // Default value can be changed in case of reallocation, service region change
  useEffect(() => {
    setInputValue(defaultInputValue);
  }, [defaultInputValue]);

  const [, scrollerRef] = useInfiniteScroll({
    hasMore: hasNextPage,
    isEnabled: isAutocompleteOpen,
    shouldUseLoader: false,
    onLoadMore: fetchNextPage,
  });

  const facilitiesList = useMemo(() => {
    const sortedList = sortItems ? [...list].sort(sortItems) : list;

    if (!selectedItem) return sortedList;

    const filteredItems = sortedList.filter((a) => a.id !== selectedItem.id);

    return [selectedItem, ...filteredItems];
  }, [list, selectedItem, sortItems]);

  const onItemSelect = (key: string | null) => {
    if (!key) {
      setSelectedItem(null);
      onSelect(undefined);
      setInputValue('');
      onClear?.();
      return;
    }

    const item = facilitiesList.find((l) => l.id === key);
    onSelect(item);
    setSelectedItem(item || null);

    if (item) {
      const displayName = getDisplayName?.(item) ?? item.name;
      setInputValue(displayName as string);
    }
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, ref, onChange, ...field }, fieldState }) => {
        const { invalid, error } = fieldState;
        const validationProps = validateOnSubmit
          ? { isInvalid: invalid, errorMessage: error?.message }
          : getInputValidationErrorProps(fieldState);

        return (
          <Autocomplete
            isDisabled={isDisabled}
            placeholder={placeholder}
            aria-label={ariaLabel}
            scrollRef={scrollerRef}
            items={facilitiesList}
            isLoading={isLoading}
            {...validationProps}
            inputValue={inputValue}
            onInputChange={(text) => {
              setInputValue(text);
              if (!text) {
                onChange('');
                onItemSelect(null);
              }
            }}
            selectedKey={value || null}
            onSelectionChange={(key) => {
              const newValue = isNull(key) ? '' : key.toString();
              onChange(newValue);
              onItemSelect(newValue);
            }}
            onOpenChange={setAutocompleteOpen}
            {...field}
          >
            {/* @ts-expect-error HeroUI v2 CollectionChildren type incompatible with React 19 ReactElement */}
            {renderListItem}
          </Autocomplete>
        );
      }}
    />
  );
};
